import type { Components } from 'react-markdown';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { HighRiskVerticalAnalysisResult } from '@ballerine/digital-footprint-schemas';

import { useForceExpandAccordions } from '@/common/providers/PrintContext';
import { CheckCircle, WarningFilledSvg } from '@/components/atoms';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/molecules/Accordion';

type HighRiskVerticalAnalysisSectionProps = {
  highRiskVerticalAnalysisResults?: HighRiskVerticalAnalysisResult[] | null;
};

const formatVerticalDisplayName = (vertical: string, verticalDisplayName?: string) => {
  if (verticalDisplayName?.trim()) {
    return verticalDisplayName;
  }

  if (vertical === 'ip-rights-and-brands') {
    return 'Validation & Evidence Review';
  }

  return vertical
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const sanitizeSections = (
  sections: HighRiskVerticalAnalysisResult['sections'],
): NonNullable<HighRiskVerticalAnalysisResult['sections']> => {
  if (!sections) {
    return [];
  }

  return sections.filter(section =>
    Boolean(section.displayName?.trim() && section.content?.trim()),
  );
};

const hasRenderableContent = (result: HighRiskVerticalAnalysisResult) => {
  const hasSummary = Boolean(result.summary?.trim());
  const hasSections = sanitizeSections(result.sections).length > 0;

  return hasSummary || hasSections;
};

const getStatusIcon = (status: 'clear' | 'alert') => {
  if (status === 'alert') {
    return <WarningFilledSvg className="h-6 w-6 pt-[2px]" />;
  }

  return (
    <CheckCircle
      size={20}
      className="text-slate-400"
      containerProps={{ className: 'bg-slate-100 ml-[2.5px]' }}
    />
  );
};

export const HighRiskVerticalAnalysisSection: React.FC<HighRiskVerticalAnalysisSectionProps> = ({
  highRiskVerticalAnalysisResults,
}) => {
  const forceExpandAccordions = useForceExpandAccordions();

  if (!highRiskVerticalAnalysisResults || highRiskVerticalAnalysisResults.length === 0) {
    return null;
  }

  const results = highRiskVerticalAnalysisResults
    .map(result => ({
      ...result,
      sections: sanitizeSections(result.sections),
    }))
    .filter(hasRenderableContent);

  if (results.length === 0) {
    return null;
  }

  return (
    <div>
      {results.map((result, index) => {
        const verticalTitle = formatVerticalDisplayName(
          result.vertical,
          result.verticalDisplayName,
        );
        const summary = result.summary?.trim() ?? '';
        const sections = result.sections ?? [];

        return (
          <section
            key={`${result.vertical}-${index}`}
            data-testid="high-risk-vertical"
            className="mb-6 rounded-2xl border border-b border-slate-100 bg-white p-10 shadow-sm"
          >
            <p className="text-sm text-slate-500">High Risk Vertical Analysis</p>
            <h4 className="my-3 text-xl font-semibold text-slate-900">{verticalTitle}</h4>
            {summary && (
              <div className="mb-4 text-sm leading-relaxed text-slate-600">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={summaryComponents}>
                  {summary}
                </ReactMarkdown>
              </div>
            )}

            {sections.length > 0 ? (
              <Accordion
                type="multiple"
                className="space-y-2"
                defaultValue={
                  forceExpandAccordions
                    ? sections.map((section, i) => `${result.vertical}-${section.displayName}-${i}`)
                    : undefined
                }
              >
                {sections.map((section, sectionIndex) => {
                  const accordionValue = `${result.vertical}-${section.displayName}-${sectionIndex}`;

                  return (
                    <AccordionItem
                      key={accordionValue}
                      value={accordionValue}
                      className="rounded-2xl border border-slate-100 shadow-sm"
                    >
                      <AccordionTrigger className="cursor-pointer overflow-hidden rounded-2xl border border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50 hover:no-underline data-[state=open]:rounded-b-none">
                        <div className="flex w-full items-center gap-4">
                          <div className="flex-shrink-0">{getStatusIcon(section.status)}</div>
                          <div className="flex-1 text-sm font-normal text-slate-900">
                            {section.displayName}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-5 pt-1">
                        <div className="pl-9 text-sm leading-relaxed text-slate-700">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={markdownComponents}
                          >
                            {section.content.trim()}
                          </ReactMarkdown>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

const summaryComponents: Components = {
  p: ({ node, ...props }) => <p className="my-1.5 text-sm leading-relaxed text-black" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-slate-700" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="text-xs font-medium text-indigo-600 underline-offset-2 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
};

const markdownComponents: Components = {
  p: ({ node, ...props }) => <p className="my-2 text-sm text-slate-700" {...props} />,
  h2: ({ node, ...props }) => (
    <h2 className="mt-3 text-base font-semibold text-slate-800" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mt-2 text-sm font-semibold text-slate-800" {...props} />
  ),
  ul: ({ node, ...props }) => <ul className="my-2 list-disc space-y-1.5 pl-5" {...props} />,
  ol: ({ node, ...props }) => <ol className="my-2 list-decimal space-y-1.5 pl-5" {...props} />,
  li: ({ node, ...props }) => <li className="text-sm text-slate-700" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="mx-1 inline-block rounded-full bg-indigo-50 px-2 py-0 text-[0.7rem] text-neutral-400 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-2 border-l-2 border-indigo-100 pl-4 italic text-slate-700"
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <div className="my-4 overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => (
    <tr className="border-b border-slate-200 last:border-b-0" {...props} />
  ),
  th: ({ node, ...props }) => (
    <th
      className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td className="px-3 py-2 align-top text-sm text-slate-700" {...props} />
  ),
};
